import type { Doc } from "building-types/doc";
import type { BlockHandle } from "building-types/handles/docEntityHandleTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";

/**
 * Returns room IDs for a level, or all room IDs if levelId is undefined.
 * Excludes entity-ref'd rooms. If levelId is set, only rooms in blocks on that level are returned.
 */
export function docGetRoomIdsForLevel(
  doc: Pick<Doc, "rooms" | "blocks" | "entityRefs">,
  levelId: string | undefined,
): string[] {
  const rooms = Object.values(doc.rooms).filter((r) => !doc.entityRefs?.[r.id]);
  if (!levelId) {
    return rooms.sort((a, b) => a.sortIndex - b.sortIndex).map((r) => r.id);
  }
  return rooms
    .filter((r) => r.parentId && doc.blocks[r.parentId]?.levelId === levelId)
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .map((r) => r.id);
}

export function docGetBlockRooms(doc: Pick<Doc, "rooms" | "entityRefs">, { id: blockId }: BlockHandle): StaticRoom[] {
  return Object.values(doc.rooms)
    .filter((x) => x.parentId === blockId)
    .filter((x) => !doc.entityRefs[x.id])
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

export function docGetBlockRoomIds(doc: Pick<Doc, "rooms" | "entityRefs">, handle: BlockHandle): string[] {
  return docGetBlockRooms(doc, handle).map((x) => x.id);
}

export function docGetUnplacedRoomIds(doc: Pick<Doc, "rooms" | "blocks" | "entityRefs">): string[] {
  return Object.values(doc.rooms)
    .filter((room) => (!room.parentId || !doc.blocks[room.parentId]) && !doc.entityRefs[room.id]) // don't show if doc ref'd room
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .map((room) => room.id);
}

export function roomHasValidBlock(doc: Pick<Doc, "blocks">, room: StaticRoom | null) {
  if (!room) {
    return false;
  }
  return !!room.parentId && !!doc.blocks[room.parentId];
}

export function docGetRoomLevelId(doc: Doc, roomId: string): string | undefined {
  const room = doc.rooms[roomId];
  if (!room?.parentId) {
    return undefined;
  }
  return doc.blocks[room.parentId]?.levelId;
}
